import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, RotateCw, AlertTriangle, Ban, Search, FileText, Link2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, formatMoney } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import { StatusChip } from "@/components/common/StatusChip";
import { Section } from "@/features/workorders/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ESTADO_TONE = { pendiente: "gray", estimado: "gray", emitida: "blue", enviada: "amber", cobrada: "green", error: "red", estimado_anulado: "gray" };
const BRAND_LABEL = { bb: "B&B", betos: "Betos" };

export const QboInvoiceSection = ({ wo, factura, onDone }) => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const canUpdate = can("finance", "update");
  const canEmit = canUpdate && ["aprobada", "cerrada"].includes(wo.status);
  const isSimulator = !factura || factura.provider !== "intuit";
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [checkPayOpen, setCheckPayOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pagos, setPagos] = useState(null);

  const { data: detalle } = useQuery({
    queryKey: ["qbo-invoice-detail", wo.id],
    queryFn: async () => (await api.get(`/qbo/invoices/${wo.id}/detail`)).data,
    enabled: detailOpen,
  });

  const refresh = () => {
    onDone?.();
    qc.invalidateQueries({ queryKey: ["work-order", wo.id] });
  };

  // Fix T (2026-10-05): link de la factura para el cliente. La ventana se abre antes de la
  // llamada para que el navegador no la bloquee.
  const clientLink = async (abrir) => {
    const ventana = abrir ? window.open("about:blank", "_blank") : null;
    try {
      const { data } = await api.post(`/qbo/invoices/${wo.id}/client-link`);
      const url = data.url.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
      if (ventana) {
        ventana.location.href = url;
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* portapapeles bloqueado */
      }
      toast.success(t("wo.detail.qbo.client_link_copied"));
    } catch (e) {
      if (ventana) ventana.close();
      toast.error(apiError(e, t("app.error")));
    }
  };

  const setBrand = useMutation({
    mutationFn: (brand) => api.patch(`/qbo/work-orders/${wo.id}/billing-brand`, { billing_brand: brand }),
    onSuccess: () => { toast.success(t("app.saved")); refresh(); },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const issue = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/issue`),
    onSuccess: ({ data }) => {
      toast[data.estado === "error" ? "error" : "success"](
        data.estado === "error" ? data.error : t("wo.detail.qbo.issued"));
      refresh();
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const send = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/send`),
    onSuccess: () => { toast.success(t("wo.detail.qbo.sent")); refresh(); },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const retry = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/retry`),
    onSuccess: ({ data }) => {
      toast[data.estado === "error" ? "error" : "success"](
        data.estado === "error" ? data.error : t("wo.detail.qbo.issued"));
      refresh();
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const simulatePay = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/simulate-pay`),
    onSuccess: () => { toast.success(t("wo.detail.qbo.paid")); refresh(); },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const voidAndReissue = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/void-and-reissue`),
    onSuccess: ({ data }) => {
      toast[data.estado === "error" ? "error" : "success"](
        data.estado === "error" ? data.error : t("wo.detail.qbo.voided_and_reissued"));
      refresh();
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const checkPayment = useMutation({
    mutationFn: () => api.post(`/qbo/invoices/${wo.id}/check-payment`, { desde, hasta }),
    onSuccess: ({ data }) => setPagos(data.pagos || []),
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  if (!wo.billing_brand && !factura && !canEmit) return null;

  return (
    <Section title={t("wo.detail.qbo.title")} testId="wo-qbo">
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.brand")}</p>
          {canUpdate && (!factura || factura.estado === "error") ? (
            <Select value={wo.billing_brand || "unset"} onValueChange={(v) => setBrand.mutate(v)}>
              <SelectTrigger data-testid="wo-qbo-brand-select" className="mt-1 h-8 text-[12.5px]">
                <SelectValue placeholder={t("wo.detail.qbo.choose_brand")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bb">B&amp;B</SelectItem>
                <SelectItem value="betos">Betos</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="mt-1 text-[13px] text-ink">{wo.billing_brand ? BRAND_LABEL[wo.billing_brand] : "—"}</p>
          )}
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("app.status")}</p>
          <div className="mt-1.5">
            <StatusChip
              testId="wo-qbo-estado"
              tone={ESTADO_TONE[factura?.estado || wo.qbo_estado] || "gray"}
              label={t(`wo.detail.qbo.estados.${factura?.estado || wo.qbo_estado || "sin_factura"}`)}
            />
          </div>
        </div>
        {factura?.numero_estimado && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.estimate_number")}</p>
            <p className="mt-1 font-mono text-[13px] text-ink" data-testid="wo-qbo-estimado-numero">{factura.numero_estimado}</p>
          </div>
        )}
        {/* Fix M (2026-09-28): la orden como proyecto y el costo del sub cargado al proyecto. */}
        {factura?.qbo_proyecto_nombre && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.project")}</p>
            <p className="mt-1 text-[13px] text-ink" data-testid="wo-qbo-proyecto">{factura.qbo_proyecto_nombre}</p>
          </div>
        )}
        {(factura?.qbo_bill_numero || wo.qbo_bill_error) && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.sub_bill")}</p>
            {factura?.qbo_bill_numero ? (
              <p className="mt-1 font-mono text-[13px] text-ink" data-testid="wo-qbo-bill">
                {factura.qbo_bill_numero}{factura.costo_sub_cents ? ` · ${formatMoney(factura.costo_sub_cents)}` : ""}
              </p>
            ) : (
              <p className="mt-1 text-[12px] text-state-critical" data-testid="wo-qbo-bill-error">{wo.qbo_bill_error}</p>
            )}
          </div>
        )}
        {factura?.estimado_anulado && (
          <div className="col-span-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.estados.estimado_anulado")}</p>
            <p className="mt-1 text-[12.5px] text-ink-secondary" data-testid="wo-qbo-estimado-anulado">
              {factura.estimado_anulado_motivo} · {factura.estimado_anulado_por_nombre} · {formatDateTime(factura.estimado_anulado_at)}
            </p>
          </div>
        )}
        {factura?.numero && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("finance.billing.invoice_number")}</p>
            <p className="mt-1 font-mono text-[13px] text-ink" data-testid="wo-qbo-numero">{factura.numero}</p>
          </div>
        )}
        {factura?.monto_cents ? (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.price")}</p>
            <Amount cents={factura.monto_cents} className="mt-1 text-[15px]" />
          </div>
        ) : null}
        {factura?.fecha_emision && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("finance.billing.invoice_date")}</p>
            <p className="mt-1 text-[13px] text-ink">{formatDateTime(factura.fecha_emision)}</p>
          </div>
        )}
        {factura?.fecha_envio && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.sent_date")}</p>
            <p className="mt-1 text-[13px] text-ink" data-testid="wo-qbo-fecha-envio">{formatDateTime(factura.fecha_envio)} · {factura.enviada_por_nombre}</p>
          </div>
        )}
        {factura?.fecha_cobro && (
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("finance.billing.collection_date")}</p>
            <p className="mt-1 text-[13px] text-ink">{formatDateTime(factura.fecha_cobro)}</p>
          </div>
        )}
      </div>

      {factura?.sin_estimado_previo && (
        <p className="mt-2 text-[11.5px] text-ink-tertiary" data-testid="wo-qbo-sin-estimado-previo">
          {t("wo.detail.qbo.sin_estimado_previo")}
        </p>
      )}

      {factura?.link && (
        <a href={factura.link} target="_blank" rel="noreferrer" data-testid="wo-qbo-link"
          className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-brand hover:text-brand-hover">
          {t("wo.detail.qbo.view_in_qbo")} <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      {!factura?.link && factura?.numero && isSimulator && (
        <button data-testid="wo-qbo-view-simulated" onClick={() => setDetailOpen(true)}
          className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-brand hover:text-brand-hover">
          {t("wo.detail.qbo.view_simulated")} <FileText className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Fix T (2026-10-05): link de la factura para el CLIENTE (no el portal del sub). */}
      {factura?.numero && ["emitida", "enviada", "cobrada"].includes(factura?.estado) && (
        <div className="mt-3 border-t border-line pt-3" data-testid="wo-qbo-client-link">
          <p className="text-[11.5px] text-ink-tertiary">{t("wo.detail.qbo.client_link_hint")}</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            <Button data-testid="wo-qbo-client-link-copy" size="sm" variant="outline" onClick={() => clientLink(false)}
              className="h-8 rounded-control text-[12.5px]">
              <Link2 className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.client_link_copy")}
            </Button>
            <Button data-testid="wo-qbo-client-link-open" size="sm" variant="outline" onClick={() => clientLink(true)}
              className="h-8 rounded-control text-[12.5px]">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.client_link_open")}
            </Button>
          </div>
        </div>
      )}

      {factura?.estado === "error" && (
        <p className="mt-3 flex items-start gap-1.5 border-l-2 border-state-critical bg-state-critical-soft px-2.5 py-1.5 text-[11.5px] font-medium text-state-critical" data-testid="wo-qbo-error">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {factura.error}
          {factura.cliente_inactivo && (
            <span className="block font-normal" data-testid="wo-qbo-cliente-inactivo-hint">
              {t("wo.detail.qbo.cliente_inactivo_hint")}
            </span>
          )}
        </p>
      )}

      {canUpdate && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(!factura || factura.estado === "estimado") && canEmit && (
            <Button data-testid="wo-qbo-issue" size="sm" disabled={issue.isPending}
              onClick={() => issue.mutate()} className="h-8 rounded-control bg-brand text-[12.5px] text-paper hover:bg-brand-hover">
              {t("wo.detail.qbo.issue")}
            </Button>
          )}
          {factura?.estado === "error" && (
            <Button data-testid="wo-qbo-retry" size="sm" variant="outline" disabled={retry.isPending}
              onClick={() => retry.mutate()} className="h-8 rounded-control text-[12.5px]">
              <RotateCw className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.retry")}
            </Button>
          )}
          {["emitida", "enviada"].includes(factura?.estado) && isSimulator && (
            <Button data-testid="wo-qbo-simulate-pay" size="sm" variant="outline" disabled={simulatePay.isPending}
              onClick={() => simulatePay.mutate()} className="h-8 rounded-control text-[12.5px]">
              {t("wo.detail.qbo.simulate_pay")}
            </Button>
          )}
          {factura?.estado === "emitida" && (
            <Button data-testid="wo-qbo-send" size="sm" disabled={send.isPending}
              onClick={() => send.mutate()} className="h-8 rounded-control bg-brand text-[12.5px] text-paper hover:bg-brand-hover">
              {t("wo.detail.qbo.send")}
            </Button>
          )}
          {["emitida", "enviada", "cobrada"].includes(factura?.estado) && (
            <>
              <Button data-testid="wo-qbo-void-reissue" size="sm" variant="outline"
                disabled={voidAndReissue.isPending}
                onClick={() => setVoidConfirmOpen(true)}
                className="h-8 rounded-control text-[12.5px] text-state-critical hover:bg-state-critical-soft">
                <Ban className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.void_and_reissue")}
              </Button>
              <Button data-testid="wo-qbo-check-payment" size="sm" variant="outline"
                onClick={() => { setPagos(null); setCheckPayOpen(true); }}
                className="h-8 rounded-control text-[12.5px]">
                <Search className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.check_payment")}
              </Button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        testId="wo-qbo-void-reissue-confirm"
        open={voidConfirmOpen}
        onOpenChange={setVoidConfirmOpen}
        title={t("wo.detail.qbo.void_and_reissue")}
        description={t("wo.detail.qbo.void_and_reissue_confirm")}
        destructive
        onConfirm={() => { setVoidConfirmOpen(false); voidAndReissue.mutate(); }}
      />

      <Dialog open={checkPayOpen} onOpenChange={setCheckPayOpen}>
        <DialogContent data-testid="wo-qbo-check-payment-dialog">
          <DialogHeader>
            <DialogTitle>{t("wo.detail.qbo.check_payment")}</DialogTitle>
            <DialogDescription>{t("wo.detail.qbo.check_payment_desc")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="mb-1 text-[11.5px] text-ink-tertiary">{t("wo.detail.qbo.since")}</p>
              <Input data-testid="wo-qbo-check-payment-desde" type="date" value={desde}
                onChange={(e) => setDesde(e.target.value)} className="h-9 text-[13px]" />
            </div>
            <div>
              <p className="mb-1 text-[11.5px] text-ink-tertiary">{t("wo.detail.qbo.until")}</p>
              <Input data-testid="wo-qbo-check-payment-hasta" type="date" value={hasta}
                onChange={(e) => setHasta(e.target.value)} className="h-9 text-[13px]" />
            </div>
          </div>
          {pagos !== null && (
            <div className="mt-2 max-h-48 overflow-y-auto text-[12.5px]" data-testid="wo-qbo-check-payment-results">
              {pagos.length === 0 ? (
                <p className="text-ink-tertiary">{t("wo.detail.qbo.no_payments_found")}</p>
              ) : pagos.map((p) => (
                <div key={p.qbo_payment_id} className="flex justify-between border-b border-line py-1.5">
                  <span className="text-ink-secondary">{formatDateTime(p.fecha)}</span>
                  <span className="font-mono text-ink">{formatMoney(p.monto_cents)}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="wo-qbo-check-payment-submit" size="sm" disabled={!desde || !hasta || checkPayment.isPending}
              onClick={() => checkPayment.mutate()} className="h-8 rounded-control bg-brand text-[12.5px] text-paper hover:bg-brand-hover">
              {t("app.search")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="wo-qbo-detail-dialog" className="max-w-lg rounded-surface">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("wo.detail.qbo.simulated_title", { numero: detalle?.numero || "" })}</DialogTitle>
            <DialogDescription>{t("wo.detail.qbo.simulated_desc")}</DialogDescription>
          </DialogHeader>
          {detalle && (
            <div className="space-y-3 text-[13px]">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.lines")}</p>
                <ul className="mt-1 divide-y divide-line" data-testid="wo-qbo-detail-lines">
                  {(detalle.lineas || []).map((l, i) => (
                    <li key={i} className="flex justify-between gap-3 py-1.5">
                      <span className="text-ink">{l.descripcion}</span>
                      <span className="shrink-0 font-mono tabular-nums text-ink-secondary">{formatMoney(l.monto_cents)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.customer_memo")}</p>
                <p className="mt-1 text-ink" data-testid="wo-qbo-detail-customer-memo">{detalle.customer_memo || "—"}</p>
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">{t("wo.detail.qbo.private_note")}</p>
                <p className="mt-1 text-ink-secondary" data-testid="wo-qbo-detail-private-note">{detalle.private_note || "—"}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="h-9 text-[13px]">
              {t("app.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
};
